import React, { useContext, useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import _ from "lodash"
import PageHeader from "../../components/PageHeader"
import { UserContext } from "../../context/userContext"
import { GET_USERS_BY_ENTERPRISEID, GET_ORGANIZATIONS } from "./queries"
import { useQuery } from "@apollo/client"
import GenericTable from "../../components/DataTable/GenericTable"
import { useFilters } from "../../components/Filters/useFilters"
import FilterHeader from "../../components/DataTable/FilterHeader"
import withDataFetching from "../../components/DataTable/withDataFetching"

import { SplitButton } from "primereact/splitbutton"
import ROUTES from "../../components/Routes/routesContants"
import { LoaderMedium } from "../../components/Loaders"
import styles from "../../styles/Listing.module.css"
import { Button } from "primereact/button"

const ID_TEMPLATE = "staff"

const ListUsers = () => {
  const userCtx = useContext(UserContext)
  const navigate = useNavigate()
  const [roles, setRoles] = useState([])

  const {
    filters,
    globalFilterValue,
    onGlobalFilterChange,
    clearGlobalFilter,
    onFilterChange,
    clearAllFilters,
  } = useFilters()

  const { data: userData, loading: usersLoading } = useQuery(
    GET_USERS_BY_ENTERPRISEID,
    {
      variables: {
        staff_id: userCtx.session.user?.staff_id,
        enterprise_id: userCtx.session.user?.enterprise_id,
      },
      skip: !userCtx.session.user.staff_id,
      fetchPolicy: "network-only",
    }
  )

  const { data: orgData, loading: orgsLoading } = useQuery(GET_ORGANIZATIONS, {
    variables: {
      staff_id: userCtx.session.user?.staff_id,
      organization_id: userCtx.session.user?.organization_id,
    },
    skip: !userCtx.session.user.staff_id,
    fetchPolicy: "network-only",
  })

  useEffect(() => {
    if (userData) {
      const rolesOptions = _.map(
        userData.users,
        ({ staff_role_name }) => staff_role_name
      )
        .filter((value, index, self) => self.indexOf(value) === index)
        .map((role) => ({ label: role, value: role }))
      setRoles([{ label: "All", value: "" }, ...rolesOptions])
    }
  }, [userData])

  if (usersLoading || orgsLoading) return <LoaderMedium />

  const organizations = _.map(
    orgData?.organizations,
    ({ organization_id, organization_name }) => ({
      label: organization_name,
      value: organization_name,
    })
  )

  const users = _.map(userData?.users, (staff) => ({
    ...staff,
    full_name: `${_.unescape(staff.staff_name_first)} ${_.unescape(
      staff.staff_name_last
    )}`,
    organization_name: _.unescape(staff.organization_name),
    actions: (
      <SplitButton
        label="View"
        onClick={async () => {
          await userCtx.changeCurrentStaffMembershipId(
            staff?.staff_membership_id
          ) // Set the staff membership ID
          await userCtx.changeOrganization(staff?.organization_id) // Change the organization
          navigate("/enterprise/staff/profile") // Navigate to the profile page
        }}
        model={[
          {
            label: "Edit",
            command: async () => {
              await userCtx.changeCurrentStaffMembershipId(
                staff?.staff_membership_id
              )
              navigate(
                `/enterprise/form/${ID_TEMPLATE}/1/${staff?.staff_id_stamp}`
              )
            },
          },
        ]}
      />
    ),
  }))

  const filterConfig = [
    {
      label: "Organization",
      value: filters.organization_name?.value || "",
      options: organizations,
      onChange: (e) => onFilterChange("organization_name", e.value),
      onClear: () => onFilterChange("organization_name", null),
      placeholder: "Select an Organization",
      enableFilter: true,
    },
    {
      label: "Role",
      value: filters.staff_role_name?.value || "",
      options: roles,
      onChange: (e) => onFilterChange("staff_role_name", e.value),
      onClear: () => onFilterChange("staff_role_name", null),
      placeholder: "Select a Role",
    },
  ]

  const columns = [
    { field: "full_name", header: "Staff Name", sortable: true },
    { field: "organization_name", header: "Organization", sortable: true },
    { field: "staff_role_name", header: "Role", sortable: true },
    {
      field: "staff_primary_supervisor_name",
      header: "Supervisor",
      sortable: true,
    },
    {
      field: "assigned_participants",
      header: "Assigned Participants",
      sortable: true,
    },
    { field: "actions", header: "Actions" },
  ]

  return (
    <div className="main-content">
      <PageHeader
        title="Manage Staff Members"
        actions={[
          {
            to: `/enterprise/form/${ID_TEMPLATE}/1/`,
            label: "Add new member",
          },
        ]}
      />
      <GenericTable
        data={users}
        columns={columns}
        filters={filters}
        globalFilterValue={globalFilterValue}
        onGlobalFilterChange={onGlobalFilterChange}
        header={
          <FilterHeader
            filters={filterConfig}
            globalFilterValue={globalFilterValue}
            onGlobalFilterChange={onGlobalFilterChange}
            clearGlobalFilter={clearGlobalFilter}
            clearAllFilters={clearAllFilters}
          />
        }
        paginator
        rows={50}
        sortField="full_name"
        sortOrder={1}
      />
    </div>
  )
}

const WrappedListUsers = (props) => {
  const userCtx = useContext(UserContext)
  const variables = {
    staff_id: userCtx.session.user?.staff_id,
    enterprise_id: userCtx.session.user?.enterprise_id,
  }

  const EnhancedListUsers = withDataFetching(
    ListUsers,
    GET_USERS_BY_ENTERPRISEID
  )

  return <EnhancedListUsers {...props} variables={variables} />
}

export default WrappedListUsers
